/**
 * OrgAutocomplete — Searchable organization selector with "Create new org" option
 * S10 Wave 2
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { authFetchJson } from '../../lib/authFetch'
import { ORGANIZATION_TYPES, ORGANIZATION_TYPE_LABELS, type OrganizationType } from '@hos/shared/constants'

export interface Organization {
  id: number
  name: string
  type: OrganizationType
}

interface OrgAutocompleteProps {
  value: Organization | null
  onChange: (org: Organization | null) => void
  placeholder?: string
  allowCreate?: boolean
}

interface CreateOrgModalState {
  open: boolean
  name: string
  type: OrganizationType
  saving: boolean
  error: string | null
}

const ORG_TYPE_ICONS: Record<OrganizationType, string> = {
  university: '🎓',
  research_institute: '🔬',
  corporate: '🏢',
  startup: '🚀',
  vc: '💼',
  government: '🏛️',
  ngo: '🤝',
  other: '📍',
}

export function OrgAutocomplete({
  value,
  onChange,
  placeholder = 'Search organizations...',
  allowCreate = true,
}: OrgAutocompleteProps) {
  const [query, setQuery] = useState(value?.name || '')
  const [results, setResults] = useState<Organization[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [modal, setModal] = useState<CreateOrgModalState>({
    open: false,
    name: '',
    type: 'university',
    saving: false,
    error: null,
  })
  const ref = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Fetch organizations on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }

    setIsLoading(true)
    const signal = abortControllerRef.current?.signal || new AbortController().signal

    const API_BASE = import.meta.env.VITE_API_URL || ''
    authFetchJson<{ organizations: Organization[] }>(
      `${API_BASE}/api/v2/organizations?q=${encodeURIComponent(query)}`,
      { signal }
    )
      .then((data) => {
        setResults(data.organizations || [])
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch organizations:', err)
        }
      })
      .finally(() => setIsLoading(false))
  }, [query])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectOrg = (org: Organization) => {
    onChange(org)
    setQuery(org.name)
    setIsOpen(false)
  }

  const handleCreateClick = () => {
    setModal({ ...modal, open: true, name: query })
  }

  const handleCreateOrg = async () => {
    if (!modal.name.trim()) return

    setModal({ ...modal, saving: true, error: null })

    try {
      const newOrg = await authFetchJson<Organization>('/api/v2/organizations', {
        method: 'POST',
        json: {
          name: modal.name.trim(),
          type: modal.type,
        },
      })

      onChange(newOrg)
      setQuery(newOrg.name)
      setIsOpen(false)
      setModal({
        open: false,
        name: '',
        type: 'university',
        saving: false,
        error: null,
      })
      setResults([])
    } catch (err: any) {
      setModal({
        ...modal,
        saving: false,
        error: err.message || 'Failed to create organization',
      })
    }
  }

  const showCreateOption = allowCreate && query.trim() && !results.some((o) => o.name.toLowerCase() === query.toLowerCase())

  return (
    <>
      <div ref={ref} className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="
            w-full px-3 py-2
            bg-[#12122a] border border-[#2a2a44]
            rounded-lg
            text-[#e8e8f0] text-[13px]
            placeholder:text-[#5a5a6c]
            focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
          "
        />
        {isOpen && (results.length > 0 || showCreateOption || isLoading) && (
          <div className="
            absolute z-50 mt-1 w-full
            bg-[#1a1a32] border border-[#2a2a44]
            rounded-lg shadow-lg
            max-h-[280px] overflow-y-auto
          ">
            {isLoading && (
              <div className="px-3 py-3 text-center text-[13px] text-[#8b8ba0]">
                Loading...
              </div>
            )}

            {results.map((org) => (
              <button
                key={org.id}
                onClick={() => handleSelectOrg(org)}
                className="
                  w-full text-left px-3 py-3
                  hover:bg-[#2a2a44]
                  border-b border-[#2a2a44] last:border-b-0
                  transition-colors
                  flex items-center gap-2
                "
              >
                <span className="text-[14px] flex-shrink-0">
                  {ORG_TYPE_ICONS[org.type]}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-[#e8e8f0] truncate">
                    {org.name}
                  </div>
                  <div className="text-[11px] text-[#8b8ba0]">
                    {ORGANIZATION_TYPE_LABELS[org.type]}
                  </div>
                </div>
              </button>
            ))}

            {showCreateOption && (
              <button
                onClick={handleCreateClick}
                className="
                  w-full text-left px-3 py-3
                  hover:bg-[#2a2a44]
                  border-t border-[#2a2a44]
                  transition-colors
                  text-[13px] text-[#8b8ba0]
                "
              >
                <span className="text-[#E61B25] font-medium">+</span> Create new organization "{query}"
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      {modal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="
            bg-[#1a1a2e] border border-[#2a2a44]
            rounded-lg shadow-xl
            p-6 w-[90%] max-w-[400px]
            space-y-4
          ">
            <h3 className="text-[15px] font-semibold text-[#e8e8f0]">
              Create New Organization
            </h3>

            {modal.error && (
              <div className="px-3 py-2 bg-red-900/30 border border-red-700/50 rounded-lg text-[12px] text-red-300">
                {modal.error}
              </div>
            )}

            <div>
              <label className="text-[12px] font-medium text-[#a8a8c0] block mb-1">
                Name <span className="text-[#E61B25]">*</span>
              </label>
              <input
                type="text"
                value={modal.name}
                onChange={(e) => setModal({ ...modal, name: e.target.value })}
                placeholder="Organization name"
                className="
                  w-full px-3 py-2
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg
                  text-[#e8e8f0] text-[13px]
                  focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
                "
              />
            </div>

            <div>
              <label className="text-[12px] font-medium text-[#a8a8c0] block mb-1">
                Type <span className="text-[#E61B25]">*</span>
              </label>
              <select
                value={modal.type}
                onChange={(e) =>
                  setModal({ ...modal, type: e.target.value as OrganizationType })
                }
                className="
                  w-full px-3 py-2
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg
                  text-[#e8e8f0] text-[13px]
                  focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
                "
              >
                {ORGANIZATION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {ORGANIZATION_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleCreateOrg}
                disabled={!modal.name.trim() || modal.saving}
                className="
                  flex-1 px-4 py-2
                  bg-[#E61B25] text-white
                  rounded-lg
                  text-[13px] font-medium
                  hover:bg-[#d41620]
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors
                "
              >
                {modal.saving ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() =>
                  setModal({
                    open: false,
                    name: '',
                    type: 'university',
                    saving: false,
                    error: null,
                  })
                }
                className="
                  flex-1 px-4 py-2
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg
                  text-[13px] font-medium text-[#a8a8c0]
                  hover:bg-[#1a1a2e] hover:border-[#3a3a54]
                  transition-colors
                "
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
